import { useContext } from "react";
import { Box, Button } from "@mui/material";

// components
import { TooltipCustom } from "../ui/tooltip-custom";

// services
import { useUserContext } from "../../services/context/user-context";
import { saveLocalState } from "../../utils/local-storage-utils";
import { luckyorangeTrackEven } from "../../services/lucky-orange/luckyorange-events";
import MapContext from "../../services/context/map-context";
import { useAppContext } from "../../services/context/app-context";

// constants
import { DRAWMODE } from "../../constants/site-settings.constants";
import { LOCAL_STORAGE_KEYS } from "../../constants/global.constants";

// svg
import { ReactComponent as IconErase } from "../../assets/svg/erase.svg";
import { ReactComponent as IconCompass } from "../../assets/svg/compass.svg";
import { ReactComponent as IconRectangle } from "../../assets/svg/rectangle.svg";
import { ReactComponent as IconPolygon } from "../../assets/svg/polygon.svg";
import { ReactComponent as IconZoomIn } from "../../assets/svg/zoom+.svg";
import { ReactComponent as IconZoomOut } from "../../assets/svg/zoom-.svg";
import { ReactComponent as IconGPS } from "../../assets/svg/gps.svg";
import { ReactComponent as IconDimensions } from "../../assets/svg/dimensions-2.svg";
import { ReactComponent as IconShowBuilding } from "../../assets/svg/building-check-mark.svg";

// style
import "./map-toolbar.css";

export const MapToolbar = ({
  drawMode,
  setDrawMode,
  deletePropertyHandler,
  currentStep,
  isSiteSettings,
  displayDimensions,
  setDisplayDimensions,
  isFileSource,
  selectedRunner,
  property,
  propertyLocation,
}) => {
  const { viewOnly } = useUserContext();
  const { map, mapLoaded } = useContext(MapContext);
  const { showBaseBuildings, setShowBaseBuildings } = useAppContext();

  const handleZoomIn = () => {
    luckyorangeTrackEven("Map Toolbar: zoom in");
    if (map) {
      map.zoomIn();
    }
  };

  const handleZoomOut = () => {
    luckyorangeTrackEven("Map Toolbar: zoom out");
    if (map) {
      map.zoomOut();
    }
  };

  const handleResetOrientation = () => {
    luckyorangeTrackEven("Map Toolbar: reset orientation");
    if (map) {
      map.easeTo({ bearing: 0, pitch: 0 });
    }
  };

  const handleSelectTile = () => {
    if (map) {
      const currentStyle = map.getStyle().name;
      const newStyle =
        currentStyle === "Mapbox Streets"
          ? "mapbox://styles/mapbox/satellite-v9"
          : "mapbox://styles/mapbox/streets-v11";

      map.setStyle(newStyle);
    }
  };

  const handleDrawPolygon = () => {
    luckyorangeTrackEven("Map Toolbar: draw polygon");
    setDrawMode(DRAWMODE.polygon);
    deletePropertyHandler();
  };

  const handleDrawRectangle = () => {
    luckyorangeTrackEven("Map Toolbar: draw rectangle");
    setDrawMode(DRAWMODE.rectangle);
    deletePropertyHandler();
  };

  const handleDelete = () => {
    luckyorangeTrackEven("Map Toolbar: delete polygon");
    deletePropertyHandler();
  };

  const handleFlyToProperty = () => {
    luckyorangeTrackEven("Map Toolbar: fly to property");

    const coordinates =
      propertyLocation && Object.hasOwn(propertyLocation, "lat")
        ? [propertyLocation?.long, propertyLocation?.lat]
        : propertyLocation && Object.hasOwn(propertyLocation, "_latitude")
          ? [propertyLocation?._longitude, propertyLocation?._latitude]
          : null;
          
    if (coordinates) map.flyTo({ center: coordinates, zoom: 17 });
  };

  const handleDisplayDimensions = () => {
    luckyorangeTrackEven("Map Toolbar: display dimensions");
    setDisplayDimensions((prev) => !prev);
  };

  const handleShowBaseBuildings = () => {
    setShowBaseBuildings((prev) => !prev);
    saveLocalState(LOCAL_STORAGE_KEYS.showBaseBuildings, !showBaseBuildings);
  };

  return (
    <Box className="map-toolbar">
      <TooltipCustom text={"Zoom In"} placement="top">
        <Button onClick={handleZoomIn} className="fill">
          <IconZoomIn fill="#343745" width="24px" height="24px" />
        </Button>
      </TooltipCustom>

      <TooltipCustom text={"Zoom Out"} placement="top">
        <Button onClick={handleZoomOut} className="fill">
          <IconZoomOut fill="#343745" width="24px" height="24px" />
        </Button>
      </TooltipCustom>

      <TooltipCustom text={"Align north"} placement="top">
        <Button onClick={handleResetOrientation} className="fill">
          <IconCompass fill="#343745" width="24px" height="24px" />
        </Button>
      </TooltipCustom>

      {/*  <Button disabled={true} onClick={handleSelectTile} title="Select Tile">
        T
      </Button> */}
      <TooltipCustom text={"Center to the property"} placement="top">
        <Box>
          <Button
            disabled={!propertyLocation}
            onClick={handleFlyToProperty}
            className="fill"
          >
            <IconGPS
              fill="#343745"
              /* strokeWidth={2} */ width="24px"
              height="24px"
            />
          </Button>
        </Box>
      </TooltipCustom>

      {!isSiteSettings && (
        <TooltipCustom text={"Display dimensions"} placement="top">
          <Box>
            <Button
              disabled={!property}
              onClick={handleDisplayDimensions}
              className={
                displayDimensions ? "fill stroke selected" : "fill stroke"
              }
            >
              <IconDimensions
                fill={displayDimensions ? "#fff" : "#343745"}
                stroke={displayDimensions ? "#fff" : "#343745"}
                width="24px"
                height="24px"
                /* strokeWidth={2} */
              />
            </Button>
          </Box>
        </TooltipCustom>
      )}
      {isSiteSettings && (
        <>
          <TooltipCustom text={"Draw an irregular polygon"} placement="top">
            <Box>
              <Button
                onClick={handleDrawPolygon}
                disabled={currentStep !== 1 || isFileSource || viewOnly}
                className={
                  currentStep === 1 &&
                  !isFileSource &&
                  drawMode === DRAWMODE.polygon
                    ? "fill selected"
                    : "fill"
                }
              >
                <IconPolygon
                  fill={
                    currentStep !== 1 || isFileSource || viewOnly
                      ? "#C7C7D6"
                      : drawMode === DRAWMODE.polygon
                        ? "#fff"
                        : "#343745"
                  }
                  width="24px"
                  height="24px"
                />
              </Button>
            </Box>
          </TooltipCustom>

          <TooltipCustom text={"Draw a rectangular polygon"} placement="top">
            <Box>
              <Button
                onClick={handleDrawRectangle}
                disabled={currentStep !== 1 || isFileSource || viewOnly}
                className={
                  currentStep === 1 &&
                  !isFileSource &&
                  drawMode === DRAWMODE.rectangle
                    ? "fill selected"
                    : "fill"
                }
              >
                <IconRectangle
                  fill={
                    currentStep !== 1 || isFileSource || viewOnly
                      ? "#C7C7D6"
                      : drawMode === DRAWMODE.rectangle
                        ? "#fff "
                        : "#343745"
                  }
                  width="24px"
                  height="24px"
                />
              </Button>
            </Box>
          </TooltipCustom>

          <TooltipCustom text={"Delete drawings"} placement="top">
            <Box>
              <Button
                onClick={handleDelete}
                disabled={currentStep !== 1 || viewOnly}
                className="fill"
              >
                <IconErase
                  fill={currentStep !== 1 || viewOnly ? "#C7C7D6" : "#343745"}
                  width="24px"
                  height="24px"
                />
              </Button>
            </Box>
          </TooltipCustom>
        </>
      )}
      {(
        isSiteSettings ? currentStep === 0 : !property || selectedRunner
      ) ? null : (
        <TooltipCustom
          text={
            showBaseBuildings ? "Hide base buildings" : "Show base buildings"
          }
          placement="top"
        >
          <Box>
            <Button
              className={
                showBaseBuildings
                  ? "building show stroke selected"
                  : " building hide stroke"
              }
              onClick={handleShowBaseBuildings}
            >
              <IconShowBuilding
                stroke={showBaseBuildings ? "#fff" : "#343745"}
                width="24px"
                height="24px"
              />
            </Button>
          </Box>
        </TooltipCustom>
      )}
    </Box>
  );
};
